import { defineStyle, defineStyleConfig } from '@chakra-ui/styled-system';
import { rem } from '@vimeo/bokeh-utils';
import { darken, lighten, transparentize, mix, readableColor } from 'color2k';
import { blue, red, grayscale, green } from '../tokens/colors.js';
import { semanticTokens } from '../tokens/semanticTokens.js';
import { getColor } from '../utils/getColor.js';
import { IconButton } from './iconButton.js';

const resetStyles = {
  background: "transparent",
  padding: 0,
  cursor: "pointer",
  lineHeight: "inherit",
  color: "inherit",
  fontSize: "100%",
  overflow: "visible",
  "::-moz-focus-inner": {
    border: "0 !important"
  },
  textTransform: "none"
};
const baseStyle = {
  '&[data-type="icon-button"]': {
    ...IconButton.baseStyle
  },
  ...resetStyles,
  borderRadius: "button",
  fontWeight: "bold",
  scale: 1,
  transition: "all 170ms ease-in-out",
  _disabled: {
    opacity: 0.5,
    cursor: "not-allowed"
  },
  _focusVisible: {
    outlineColor: "blue.500",
    outlineOffset: "2px"
  },
  _loading: {
    opacity: 1
  },
  _active: {
    transform: "scale(0.98)"
  }
};
const minimalVariantStyles = defineStyle((props) => {
  const { theme, color, colorMode } = props;
  const defaultColorValue = colorMode === "dark" ? "grayscale.0" : "slate.900";
  const colorValue = getColor({ theme, color: color || defaultColorValue, colorMode });
  const activeColor = darken(colorValue, 0.01);
  const darkColorBg = color ? colorValue : lighten(colorValue, 0.5);
  return {
    // Increase specificity over color prop
    "&": {
      color: colorValue
    },
    background: "transparent",
    _hover: {
      color: colorValue,
      bgColor: transparentize(colorValue, 0.9)
    },
    _active: {
      color: color ? mix(colorValue, "black", 0.15) : "white",
      bgColor: transparentize(activeColor, 0.8)
    },
    ".chakra-alert &": {
      _dark: {
        color: colorValue,
        _hover: {
          color: colorValue
        }
      }
    },
    _dark: {
      color: "white",
      bgColor: "transparent",
      _hover: {
        color: color ? readableColor(darkColorBg) : "white",
        bgColor: transparentize(darkColorBg, 0.6)
      },
      _active: {
        bgColor: transparentize(darkColorBg, 0.7)
      }
    }
  };
});
const transparentVariantStyles = defineStyle((props) => {
  const { theme, color: bgColor, colorMode } = props;
  const defaultColorValue = colorMode === "dark" ? "grayscale.500" : "slate.500";
  const colorValue = getColor({ theme, color: bgColor || defaultColorValue, colorMode });
  const activeColor = darken(colorValue, 0.15);
  return {
    // Increase specificity over color prop
    "&": {
      color: bgColor ? readableColor(transparentize(colorValue, 0.4)) : "white"
    },
    bgColor: transparentize(colorValue, 0.4),
    _hover: {
      color: bgColor ? readableColor(transparentize(colorValue, 0.325)) : "white",
      bgColor: transparentize(colorValue, 0.325)
    },
    _active: {
      bgColor: transparentize(activeColor, 0.25)
    }
  };
});
const upsellStyles = {
  bgColor: "upsell-primary",
  color: "white",
  _loading: {
    bgColor: transparentize(semanticTokens.colors["upsell-primary"].default, 0.5),
    _disabled: {
      bgColor: transparentize(semanticTokens.colors["upsell-primary"].default, 0.5),
      _hover: {
        bgColor: transparentize(semanticTokens.colors["upsell-primary"].default, 0.5)
      }
    }
  },
  _disabled: {
    bgColor: "upsell-primary",
    _hover: {
      bgColor: "upsell-primary"
    }
  },
  _hover: {
    bgColor: "purple.500"
  },
  _active: {
    bgColor: "upsell-primary"
  }
};
const tertiaryStyles = defineStyle({
  bgColor: "transparent",
  color: "text-primary",
  _loading: {
    bgColor: "rgba(255,255,255,0.5)",
    _disabled: {
      bgColor: "rgba(255,255,255,0.5)",
      _hover: {
        bgColor: "rgba(255,255,255,0.5)"
      }
    }
  },
  _hover: {
    bgColor: "darkBlueAlpha.200"
  },
  _active: {
    bgColor: "darkBlueAlpha.400"
  },
  _disabled: {
    bgColor: "transparent",
    _hover: { bgColor: "transparent" }
  },
  _dark: {
    bgColor: "transparent",
    _hover: {
      bgColor: "lightBlueAlpha.300"
    },
    _active: {
      bgColor: "lightBlueAlpha.500"
    },
    _disabled: {
      bgColor: "transparent",
      _hover: { bgColor: "transparent" }
    },
    _loading: {
      bgColor: transparentize(blue["100"], 0.5),
      _disabled: {
        bgColor: transparentize(blue["100"], 0.5),
        _hover: {
          bgColor: transparentize(blue["100"], 0.5)
        }
      }
    }
  }
});
const blurStyles = defineStyle({
  backdropFilter: "blur(var(--vimeo-blur-md))",
  "::-moz-backdrop-filter": "blur(var(--vimeo-blur-md))",
  bgColor: "grayAlpha.100",
  color: "text-primary",
  _hover: {
    background: "grayAlpha.200"
  },
  _disabled: {
    bgColor: "grayAlpha.100",
    _hover: {
      bgColor: "grayAlpha.100"
    }
  },
  _active: {
    background: "grayAlpha.200"
  },
  _dark: {
    bgColor: "grayAlpha.700",
    _hover: {
      bgColor: "grayAlpha.600"
    },
    _active: {
      bgColor: "grayAlpha.600"
    },
    _disabled: {
      bgColor: "grayAlpha.700",
      _hover: {
        bgColor: "grayAlpha.700"
      }
    }
  }
});
const primaryStyles = defineStyle({
  color: "white",
  backgroundColor: "blue.500",
  _active: {
    backgroundColor: mix(blue[500], "black", 0.12)
  },
  _hover: {
    backgroundColor: mix(blue[500], "white", 0.12),
    scale: 0.9
  }
});
const negativeStyles = defineStyle({
  backgroundColor: "red.600",
  color: "white",
  _active: {
    backgroundColor: mix(red[600], "black", 0.15)
  },
  _hover: {
    backgroundColor: mix(red[600], "white", 0.15)
  }
});
let Button = defineStyleConfig({
  baseStyle,
  // Consolidated variants: https://www.figma.com/file/X4wodo4dZU9aFhwfo0NY40/DS-Mapping?type=design&node-id=55-1103&mode=design&t=iURVtdDVgZ3ZMmz4-0
  variants: {
    primary: primaryStyles,
    brand: primaryStyles,
    secondary: {
      color: "black",
      backgroundColor: "grayscale.50",
      _hover: {
        backgroundColor: mix(grayscale[50], "white", 0.15)
      },
      _active: {
        backgroundColor: mix(grayscale[50], "black", 0.15)
      },
      _dark: {
        color: "white",
        backgroundColor: "grayscale.700",
        _hover: {
          backgroundColor: mix(grayscale[700], "white", 0.15)
        },
        _active: {
          backgroundColor: mix(grayscale[700], "black", 0.15)
        }
      }
    },
    positive: {
      backgroundColor: "green.500",
      color: "white",
      _active: {
        backgroundColor: mix(green[500], "black", 0.15)
      },
      _hover: {
        backgroundColor: mix(green["500"], "white", 0.15)
      }
    },
    //@deprecated: use destructive instead
    negative: negativeStyles,
    destructive: negativeStyles,
    outline: {
      border: "1px solid",
      borderColor: "slate.900",
      backgroundColor: "transparent",
      color: "slate.900",
      _active: {
        backgroundColor: "transparent"
      },
      _dark: {
        color: "white",
        borderColor: "white",
        _hover: {
          backgroundColor: "rgba(74, 108, 125, 0.2)"
        }
      },
      _hover: {
        backgroundColor: "slate.50"
      }
    },
    transparent: transparentVariantStyles,
    minimal: minimalVariantStyles,
    upsell: upsellStyles,
    tertiary: tertiaryStyles,
    blur: blurStyles
  },
  sizes: {
    xs: {
      svg: {
        height: "2xs",
        width: "2xs"
      },
      '&[data-type="icon-button"]': {
        ...IconButton.sizes.xs
      },
      height: "xs",
      px: "75",
      fontSize: "text-xs",
      borderRadius: 4
    },
    sm: {
      svg: {
        height: "2xs",
        width: "2xs"
      },
      '&[data-type="icon-button"]': {
        ...IconButton.sizes.sm
      },
      height: "sm",
      px: "100",
      fontSize: "text-sm",
      borderRadius: 6,
      ".chakra-button__spinner": {
        fontSize: "1rem"
      }
    },
    md: {
      svg: {
        height: "xs",
        width: "xs"
      },
      '&[data-type="icon-button"]': {
        ...IconButton.sizes.md
      },
      height: "md",
      px: "200",
      fontSize: "text-sm",
      borderRadius: 8,
      ".chakra-button__spinner": {
        fontSize: "1.25rem"
      }
    },
    lg: {
      '&[data-type="icon-button"]': {
        ...IconButton.sizes.lg
      },
      svg: {
        height: "xs",
        width: "xs"
      },
      height: "lg",
      px: rem(20),
      fontSize: "text",
      borderRadius: 10,
      ".chakra-button__spinner": {
        fontSize: "1.25rem"
      }
    },
    xl: {
      '&[data-type="icon-button"]': {
        ...IconButton.sizes.xl
      },
      svg: {
        height: "xs",
        width: "xs"
      },
      height: "xl",
      px: rem(20),
      fontSize: "header-xl",
      borderRadius: 10,
      ".chakra-button__spinner": {
        fontSize: "1.75rem"
      }
    }
  },
  defaultProps: {
    variant: "primary",
    size: "md"
  }
});

export { Button };
